import clsx from "clsx";
import { ArrowLeft, Clock, Server, User, TrendingUp, Cat, DollarSign } from "lucide-react";
import { useMemo, useState } from "react";

import { useMoriartyEnvironments } from "@/hooks/useMoriarty";
import { useStore } from "@/lib/store";
import type { MoriartyEnvironment } from "@/lib/types";
import { formatCost } from "@/lib/utils";

import { SidebarToggleButton } from "../SidebarToggleButton";

// ---------------------------------------------------------------------------
// Time period filtering
// ---------------------------------------------------------------------------

type Period = "today" | "week" | "month" | "all";

function getPeriodStart(period: Period): Date | null {
  if (period === "all") return null;
  const now = new Date();
  switch (period) {
    case "today": {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case "week": {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case "month": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      d.setHours(0, 0, 0, 0);
      return d;
    }
  }
}

function filterByPeriod(envs: MoriartyEnvironment[], period: Period): MoriartyEnvironment[] {
  const start = getPeriodStart(period);
  if (!start) return envs;
  return envs.filter((e) => {
    const created = new Date(e.createdAt);
    const terminated = e.terminatedAt ? new Date(e.terminatedAt) : new Date();
    // Include if it overlaps with the period
    return terminated >= start && created <= new Date();
  });
}

// ---------------------------------------------------------------------------
// Aggregation helpers
// ---------------------------------------------------------------------------

type UserCost = { email: string; cost: number; hours: number; envCount: number };
type InstanceCost = { type: string; cost: number; hours: number; envCount: number };
type DayCost = { day: string; cost: number; envCount: number };

function aggregateByUser(envs: MoriartyEnvironment[]): UserCost[] {
  const map = new Map<string, UserCost>();
  for (const env of envs) {
    const existing = map.get(env.createdBy) ?? {
      email: env.createdBy,
      cost: 0,
      hours: 0,
      envCount: 0,
    };
    existing.cost += env.estimatedCostUsd;
    existing.hours += env.uptimeMinutes / 60;
    existing.envCount += 1;
    map.set(env.createdBy, existing);
  }
  return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
}

function aggregateByInstanceType(envs: MoriartyEnvironment[]): InstanceCost[] {
  const map = new Map<string, InstanceCost>();
  for (const env of envs) {
    const existing = map.get(env.instanceType) ?? {
      type: env.instanceType,
      cost: 0,
      hours: 0,
      envCount: 0,
    };
    existing.cost += env.estimatedCostUsd;
    existing.hours += env.uptimeMinutes / 60;
    existing.envCount += 1;
    map.set(env.instanceType, existing);
  }
  return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
}

function aggregateByDay(envs: MoriartyEnvironment[]): DayCost[] {
  const map = new Map<string, DayCost>();
  for (const env of envs) {
    const day = env.createdAt.slice(0, 10);
    const existing = map.get(day) ?? { day, cost: 0, envCount: 0 };
    existing.cost += env.estimatedCostUsd;
    existing.envCount += 1;
    map.set(day, existing);
  }
  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
}

/** Format "2026-01-30" → "Jan 30" */
function formatDayLabel(day: string): string {
  const d = new Date(day + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatHours(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours % 24);
  return `${d}d ${h}h`;
}

// ---------------------------------------------------------------------------
// Bar chart component (SVG) — matches UsageStatsView pattern
// ---------------------------------------------------------------------------

function DailyCostChart({ data }: { data: DayCost[] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  const maxCost = useMemo(() => Math.max(...data.map((r) => r.cost), 0.01), [data]);

  if (data.length === 0) {
    return (
      <div className="text-cyber-muted py-8 text-center text-xs">No cost data for this period</div>
    );
  }

  const barCount = data.length;
  const chartHeight = 120;
  const xPadding = 0;
  const chartWidth = Math.max(barCount * 40, 300);
  const gap = 4;
  const barWidth = Math.max((chartWidth - xPadding * 2 - gap * (barCount - 1)) / barCount, 8);
  const yLabelHeight = 20;
  const topPad = 16;
  const totalHeight = chartHeight + yLabelHeight + topPad;

  return (
    <div className="relative w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${chartWidth} ${totalHeight}`}
        className="w-full"
        style={{ minWidth: barCount > 14 ? `${barCount * 28}px` : undefined }}
        preserveAspectRatio="xMinYMid meet"
      >
        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map((frac) => {
          const y = topPad + chartHeight - frac * chartHeight;
          return (
            <line
              key={frac}
              x1={xPadding}
              x2={chartWidth - xPadding}
              y1={y}
              y2={y}
              stroke="rgb(var(--cyber-border))"
              strokeWidth={0.5}
              strokeDasharray="3,3"
              opacity={0.5}
            />
          );
        })}

        {data.map((row, i) => {
          const x = xPadding + i * (barWidth + gap);
          const heightFrac = row.cost / maxCost;
          const barH = Math.max(heightFrac * chartHeight, 1);
          const y = topPad + chartHeight - barH;
          const isHovered = hovered === i;

          return (
            <g
              key={row.day}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: "default" }}
            >
              {/* Hover background */}
              <rect
                x={x - 2}
                y={topPad}
                width={barWidth + 4}
                height={chartHeight + yLabelHeight}
                fill={isHovered ? "rgb(var(--cyber-surface))" : "transparent"}
                rx={3}
                opacity={0.5}
              />
              {/* Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx={2}
                fill="rgb(var(--cyber-magenta))"
                opacity={isHovered ? 1 : 0.7}
              />
              {/* Hover value label */}
              {isHovered && (
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fill="rgb(var(--cyber-magenta))"
                  fontSize={9}
                  fontWeight={600}
                >
                  {formatCost(row.cost)}
                </text>
              )}
              {/* Date label */}
              <text
                x={x + barWidth / 2}
                y={topPad + chartHeight + 14}
                textAnchor="middle"
                fill="rgb(var(--cyber-muted))"
                fontSize={8}
                opacity={0.7}
              >
                {formatDayLabel(row.day)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bar row for breakdowns
// ---------------------------------------------------------------------------

function BreakdownBar({
  label,
  value,
  maxValue,
  sublabel,
  color = "bg-cyber-magenta",
}: {
  label: string;
  value: number;
  maxValue: number;
  sublabel?: string;
  color?: string;
}) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="group flex items-center gap-3">
      <div className="text-cyber-text w-28 min-w-0 shrink-0 truncate text-[11px]" title={label}>
        {label}
      </div>
      <div className="bg-cyber-surface relative h-4 min-w-0 flex-1 overflow-hidden rounded-sm">
        <div
          className={clsx("h-full rounded-sm transition-all", color)}
          style={{ width: `${Math.max(pct, 1)}%`, opacity: 0.7 }}
        />
      </div>
      <div className="text-cyber-text w-16 shrink-0 text-right font-mono text-[11px]">
        {formatCost(value)}
      </div>
      {sublabel && (
        <div className="text-cyber-muted w-20 shrink-0 text-right text-[10px]">{sublabel}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="border-cyber-border bg-cyber-surface rounded-lg border p-3">
      <div className="mb-1 flex items-center gap-1.5">
        <Icon size={12} className="text-cyber-magenta" />
        <span className="text-cyber-muted text-[10px] tracking-wider uppercase">{label}</span>
      </div>
      <div className="text-cyber-text font-mono text-lg">{value}</div>
      {sub && <div className="text-cyber-muted mt-0.5 text-[10px]">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top environments table
// ---------------------------------------------------------------------------

function TopEnvironments({ envs }: { envs: MoriartyEnvironment[] }) {
  const top = useMemo(
    () => [...envs].sort((a, b) => b.estimatedCostUsd - a.estimatedCostUsd).slice(0, 10),
    [envs],
  );

  if (top.length === 0) return null;

  return (
    <div className="border-cyber-border overflow-hidden rounded-lg border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-cyber-border bg-cyber-surface/80 border-b">
            <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Environment</th>
            <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Branch</th>
            <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Creator</th>
            <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Instance</th>
            <th className="text-cyber-muted/70 px-3 py-2 text-right font-normal">Uptime</th>
            <th className="text-cyber-muted/70 px-3 py-2 text-right font-normal">Cost</th>
          </tr>
        </thead>
        <tbody>
          {top.map((env) => (
            <tr
              key={env.id}
              className="border-cyber-border/50 hover:bg-cyber-magenta/5 border-b transition-colors"
            >
              <td className="text-cyber-text px-3 py-2 font-mono">
                {env.id.replace("moriarty-", "")}
              </td>
              <td className="text-cyber-muted max-w-[140px] truncate px-3 py-2 font-mono">
                {env.branch}
              </td>
              <td className="text-cyber-muted max-w-[120px] truncate px-3 py-2">{env.createdBy}</td>
              <td className="text-cyber-muted px-3 py-2">{env.instanceType}</td>
              <td className="text-cyber-muted px-3 py-2 text-right">
                {formatHours(env.uptimeMinutes / 60)}
              </td>
              <td className="text-cyber-text px-3 py-2 text-right font-mono">
                {formatCost(env.estimatedCostUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MoriartyCosts(): React.JSX.Element {
  const setView = useStore((state) => state.setView);
  const [period, setPeriod] = useState<Period>("month");

  const { data, isLoading } = useMoriartyEnvironments({ includeTerminated: true });
  const allEnvs = data?.environments ?? [];

  const filtered = useMemo(() => filterByPeriod(allEnvs, period), [allEnvs, period]);

  const totalCost = useMemo(() => filtered.reduce((s, e) => s + e.estimatedCostUsd, 0), [filtered]);
  const totalHours = useMemo(
    () => filtered.reduce((s, e) => s + e.uptimeMinutes / 60, 0),
    [filtered],
  );
  const avgCostPerEnv = filtered.length > 0 ? totalCost / filtered.length : 0;

  const byUser = useMemo(() => aggregateByUser(filtered), [filtered]);
  const byInstance = useMemo(() => aggregateByInstanceType(filtered), [filtered]);
  const byDay = useMemo(() => aggregateByDay(filtered), [filtered]);

  const maxUserCost = byUser.length > 0 ? byUser[0].cost : 0;
  const maxInstanceCost = byInstance.length > 0 ? byInstance[0].cost : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-cyber-border flex-shrink-0 border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <SidebarToggleButton />
            <button
              onClick={() => {
                setView("moriarty");
                const url = new URL(window.location.href);
                url.searchParams.set("view", "moriarty");
                window.history.pushState({}, "", url);
              }}
              className="text-cyber-muted hover:bg-cyber-magenta/10 hover:text-cyber-magenta rounded p-1 transition-all"
            >
              <ArrowLeft size={14} />
            </button>
            <h2 className="text-cyber-text text-sm font-medium">Cost Analysis</h2>
            <span className="border-cyber-magenta/20 bg-cyber-magenta/10 text-cyber-magenta rounded-full border px-1.5 py-0.5 text-[10px]">
              {formatCost(totalCost)}
            </span>
          </div>
        </div>
      </div>

      {/* Period tabs */}
      <div className="border-cyber-border flex flex-shrink-0 items-center gap-1 border-b px-6 py-2">
        {(
          [
            { key: "today", label: "Today" },
            { key: "week", label: "This Week" },
            { key: "month", label: "This Month" },
            { key: "all", label: "All Time" },
          ] as { key: Period; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setPeriod(t.key)}
            className={clsx(
              "rounded px-2.5 py-1 text-[10px] tracking-wider uppercase transition-all",
              period === t.key
                ? "bg-cyber-magenta/10 text-cyber-magenta"
                : "text-cyber-muted hover:text-cyber-text",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="text-cyber-muted py-12 text-center">
            <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
            Calculating costs...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Cat size={40} className="text-cyber-muted/15 mx-auto mb-4" />
            <p className="text-cyber-muted mb-1 text-sm">No environments in this period</p>
            <p className="text-cyber-muted/60 text-xs">Try selecting a wider time range.</p>
          </div>
        ) : (
          <div className="mx-auto max-w-5xl space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard
                icon={DollarSign}
                label="Total Cost"
                value={formatCost(totalCost)}
                sub={`${filtered.length} environment${filtered.length !== 1 ? "s" : ""}`}
              />
              <StatCard
                icon={Clock}
                label="Total Uptime"
                value={formatHours(totalHours)}
                sub={`avg ${formatHours(totalHours / filtered.length)}/env`}
              />
              <StatCard
                icon={TrendingUp}
                label="Avg Cost / Env"
                value={formatCost(avgCostPerEnv)}
              />
              <StatCard
                icon={User}
                label="Users"
                value={String(byUser.length)}
                sub={byUser.length > 0 ? `top: ${byUser[0].email.split("@")[0]}` : undefined}
              />
            </div>

            {/* Daily cost chart */}
            {byDay.length > 1 && (
              <div className="border-cyber-border bg-cyber-surface rounded-lg border p-4">
                <h3 className="text-cyber-muted mb-3 flex items-center gap-1.5 text-[10px] tracking-wider uppercase">
                  <TrendingUp size={12} /> Daily Cost
                </h3>
                <DailyCostChart data={byDay} />
              </div>
            )}

            {/* Breakdowns side by side */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* By User */}
              <div className="border-cyber-border bg-cyber-surface rounded-lg border p-4">
                <h3 className="text-cyber-muted mb-3 flex items-center gap-1.5 text-[10px] tracking-wider uppercase">
                  <User size={12} /> Cost by User
                </h3>
                <div className="space-y-2">
                  {byUser.map((u) => (
                    <BreakdownBar
                      key={u.email}
                      label={u.email.split("@")[0]}
                      value={u.cost}
                      maxValue={maxUserCost}
                      sublabel={`${u.envCount} env${u.envCount !== 1 ? "s" : ""}`}
                    />
                  ))}
                </div>
              </div>

              {/* By Instance Type */}
              <div className="border-cyber-border bg-cyber-surface rounded-lg border p-4">
                <h3 className="text-cyber-muted mb-3 flex items-center gap-1.5 text-[10px] tracking-wider uppercase">
                  <Server size={12} /> Cost by Instance Type
                </h3>
                <div className="space-y-2">
                  {byInstance.map((inst) => (
                    <BreakdownBar
                      key={inst.type}
                      label={inst.type}
                      value={inst.cost}
                      maxValue={maxInstanceCost}
                      sublabel={formatHours(inst.hours)}
                      color="bg-cyber-cyan"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Top environments table */}
            <div>
              <h3 className="text-cyber-muted mb-3 flex items-center gap-1.5 text-[10px] tracking-wider uppercase">
                <DollarSign size={12} /> Top Environments by Cost
              </h3>
              <TopEnvironments envs={filtered} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
